export const MAX_TRANSCRIPT_TURNS = 200;
const MAX_TURN_LENGTH = 16000;
const identifier = (value) => typeof value === 'string' && value.length > 0 && value.length <= 512;
const turnKey = (turn) => `${turn.avaId}_${turn.turnId}`;
const validTurn = (turn) => turn && identifier(turn.avaId) && Number.isSafeInteger(turn.turnId) && turn.turnId >= 0
  && Number.isSafeInteger(turn.seq) && turn.seq >= 0 && typeof turn.transcript === 'string';
const validSegment = (segment) => segment && identifier(segment.id) && Number.isFinite(segment.orderIndex)
  && Array.isArray(segment.turnIds) && segment.turnIds.every(identifier);

export const emptyTranscript = () => ({ segments: [], limited: false });

// The room agent's existing `room` stream sends complete segment updates and layout patches.
export function updateTranscript(state, message) {
  if (message?.v !== 1 || !message.payload) return state;
  const payload = message.payload;
  const turns = new Map(state.segments.flatMap((segment) => segment.turns.map((turn) => [turnKey(turn), turn])));
  let segments = new Map(state.segments.map((segment) => [segment.id, {
    id: segment.id, orderIndex: segment.orderIndex, turnIds: segment.turns.map(turnKey),
  }]));
  let limited = state.limited;

  switch (message.type) {
    case 'room.transcript.segment.update': {
      if (!identifier(payload.segmentId) || !Number.isFinite(payload.orderIndex)
        || !Array.isArray(payload.turns) || !payload.turns.every(validTurn)) return state;
      for (const turn of payload.turns) {
        const key = turnKey(turn);
        if (turn.seq < (turns.get(key)?.seq ?? -1)) continue;
        limited ||= turn.transcript.length > MAX_TURN_LENGTH;
        turns.set(key, {
          avaId: turn.avaId, turnId: turn.turnId, seq: turn.seq,
          transcript: turn.transcript.slice(0, MAX_TURN_LENGTH),
          isFinal: turn.isFinal === true || turn.isSpeechFinal === true,
          sourceKey: typeof turn.sourceKey === 'string' ? turn.sourceKey.slice(0, 512) : '',
        });
      }
      segments.set(payload.segmentId, { id: payload.segmentId, orderIndex: payload.orderIndex, turnIds: payload.turns.map(turnKey) });
      break;
    }
    case 'room.transcript.segments.replace':
      if (!Array.isArray(payload.segments) || !payload.segments.every(validSegment)) return state;
      segments = new Map(payload.segments.map((segment) => [segment.id, segment]));
      break;
    case 'room.transcript.segments.patch':
      if (!Array.isArray(payload.upsertSegments) || !payload.upsertSegments.every(validSegment)
        || !Array.isArray(payload.deleteSegmentIds) || !payload.deleteSegmentIds.every(identifier)) return state;
      payload.deleteSegmentIds.forEach((id) => segments.delete(id));
      payload.upsertSegments.forEach((segment) => segments.set(segment.id, segment));
      break;
    default: return state;
  }

  let remaining = MAX_TRANSCRIPT_TURNS;
  const next = [];
  const seen = new Set();
  const ordered = [...segments.values()].sort((left, right) => left.orderIndex - right.orderIndex || left.id.localeCompare(right.id));
  for (const segment of ordered.reverse()) {
    const available = segment.turnIds.filter((key) => {
      if (!turns.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((key) => turns.get(key));
    limited ||= available.length > remaining;
    const retained = remaining > 0 ? available.slice(-remaining) : [];
    remaining -= retained.length;
    if (retained.length) next.push({ id: segment.id, orderIndex: segment.orderIndex, turns: retained });
  }
  return { segments: next.reverse(), limited };
}
